import { UserCog } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputGroup } from '@/components/ui/input-group';
import { OnboardingStepCard } from '@/components/onboarding/OnboardingStepCard';
import { GooglePlacesAutocompleteInput } from './GooglePlacesAutocompleteInput';
import styles from '../UnifyOnboarding.module.scss';

export const BusinessDetails = ({
	isCompleted,
	isExpanded,
	isEditing,
	selectedPlace,
	addressDetails,
	formData,
	loading,
	validationErrors,
	onPlaceSelect,
	onFormChange,
	onFieldBlur,
	onSubmit,
	onEdit,
	onCancel,
	onChangeStore,
	isSignedIn,
	showPassword,
	authError,
	onEmailBlur,
	isAuthenticating
}) => {
	const isFormValid =
		addressDetails?.postcode &&
		formData.name?.trim() &&
		formData.email?.trim() &&
		(isSignedIn || (formData.password && formData.password.length >= 8));

	return (
		<OnboardingStepCard
			icon={UserCog}
			title="Your store details"
			subtitle="Tell us about your store so we can get you set up."
			isCompleted={isCompleted}
			isActive={!isCompleted}
		>
			{!isCompleted ? (
				<div className="space-y-4">
					<InputGroup label="Store name">
						<GooglePlacesAutocompleteInput onPlaceSelect={onPlaceSelect} selectedPlace={selectedPlace} />
					</InputGroup>

					{isExpanded && (
						<div className={styles.expandedForm} data-cy="business-details-expanded-form">
							<div className={styles.addressBlock}>
								<h4>Store address</h4>
								{addressDetails && (
									<div className={styles.addressDisplay} data-cy="address-display">
										<p data-cy="shop-name-display">
											<strong>{addressDetails.name}</strong>
										</p>
										<p data-cy="address-line1-display">{addressDetails.addressLine1}</p>
										{addressDetails.addressLine2 && (
											<p data-cy="address-line2-display">{addressDetails.addressLine2}</p>
										)}
										<p data-cy="city-display">{addressDetails.city}</p>
										<p data-cy="postcode-display">{addressDetails.postcode}</p>
										<p data-cy="country-display">{addressDetails.country}</p>
										<button className={styles.linkButton} data-cy="change-store-button" onClick={onChangeStore}>
											Change store
										</button>
									</div>
								)}
							</div>

							<InputGroup label="Full name" requiredField>
								<Input
									data-cy="contact-name-input"
									value={formData.name}
									onChange={(e) => onFormChange('name', e.target.value)}
									onBlur={() => onFieldBlur('name')}
									disabled={loading || isAuthenticating}
								/>
							</InputGroup>

							<InputGroup label="Email address" requiredField>
								<Input
									data-cy="contact-email-input"
									type="email"
									value={formData.email}
									onChange={(e) => onFormChange('email', e.target.value)}
									onBlur={onEmailBlur}
									disabled={loading || isAuthenticating}
								/>
							</InputGroup>

							{showPassword && !isSignedIn && (
								<InputGroup label="Create password" requiredField>
									<Input
										data-cy="password-input"
										type="password"
										value={formData.password || ''}
										onChange={(e) => onFormChange('password', e.target.value)}
										onBlur={() => onFieldBlur('password')}
										disabled={loading || isAuthenticating}
									/>
									<p className="mt-1 text-xs text-muted-foreground">At least 8 characters</p>
								</InputGroup>
							)}

							{(authError || (validationErrors && Object.keys(validationErrors).length > 0)) && (
								<div className={styles.validationErrors} data-cy="validation-errors">
									{authError && <div data-cy="auth-error">{authError}</div>}
									{validationErrors &&
										Object.values(validationErrors).map((message, index) => <div key={index}>{message}</div>)}
								</div>
							)}
							<div id="clerk-captcha" />
							{isEditing ? (
								<div className="flex flex-wrap items-center gap-3 pt-2">
									<Button
										onClick={onSubmit}
										disabled={!isFormValid || loading}
										data-cy="update-button"
										className="rounded-full px-6"
									>
										{loading ? 'Updating…' : 'Update'}
									</Button>
									<Button variant="outline" onClick={onCancel} data-cy="cancel-button" className="rounded-full px-6">
										Cancel
									</Button>
								</div>
							) : (
								<div className="pt-2">
									<Button
										onClick={onSubmit}
										disabled={!isFormValid || loading || isAuthenticating}
										data-cy="submit-button"
										className="rounded-full px-6"
									>
										{loading ? 'Creating shop…' : isAuthenticating ? 'Authenticating…' : 'Get started'}
									</Button>
									<p className="mt-4 text-xs text-muted-foreground">
										By registering, you agree to the{' '}
										<a
											className="text-primary underline-offset-2 hover:underline"
											href="https://www.near.st/legal/retailer-terms"
											target="_blank"
											rel="noreferrer"
										>
											Retailer Terms &amp; Conditions
										</a>
										.
									</p>
								</div>
							)}
						</div>
					)}
				</div>
			) : (
				<div className="flex items-start justify-between gap-4 rounded-xl bg-muted/50 px-4 py-3">
					<div>
						<p className="text-sm font-semibold text-foreground">{addressDetails?.name || 'Store name'}</p>
						{formData.name ? <p className="text-xs text-muted-foreground">{formData.name}</p> : null}
					</div>
					<button type="button" className="text-sm font-medium text-primary underline-offset-2 hover:underline" onClick={onEdit}>
						Edit details
					</button>
				</div>
			)}
		</OnboardingStepCard>
	);
};
